import { isAxiosError, type AxiosError } from 'axios'
import { ApiError } from '@/api/http/types'

const AXIOS_NETWORK_CODES = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_CONNECTION_REFUSED',
  'ERR_CONNECTION_RESET',
])

const GATEWAY_HTTP_STATUSES = new Set([502, 503, 504])

/** Application-level 5xx codes — show API message, not generic connectivity copy. */
const STRUCTURED_GATEWAY_ERROR_CODES = new Set([
  'service_unavailable',
  'audience_suggest_failed',
])

function isGatewayApiError(e: ApiError): boolean {
  if (GATEWAY_HTTP_STATUSES.has(e.status)) return true
  return (
    e.status >= 500 &&
    /unexpected error|bad gateway|gateway timeout|service unavailable/i.test(
      e.message,
    )
  )
}

function isApiErrorNetworkFailure(e: ApiError): boolean {
  if (e.code === 'network_error') return true
  if (e.status === 0) return true
  if (STRUCTURED_GATEWAY_ERROR_CODES.has(e.code)) return false
  return isGatewayApiError(e)
}

function isAxiosNetworkFailure(e: AxiosError): boolean {
  if (!e.response) return true
  if (e.code && AXIOS_NETWORK_CODES.has(e.code)) return true
  return GATEWAY_HTTP_STATUSES.has(e.response.status)
}

function isErrorMessageNetworkFailure(e: Error): boolean {
  const msg = e.message.toLowerCase()
  return (
    msg.includes('network error') ||
    msg.includes('failed to fetch') ||
    msg.includes('econnrefused')
  )
}

/** True when the browser reports no network connectivity (DevTools offline, airplane mode, etc.). */
export function isBrowserOffline(): boolean {
  return globalThis.navigator?.onLine === false
}

/** True when the request was aborted (user cancelled, offline handler, navigation, etc.). */
export function isAbortError(e: unknown): boolean {
  if (isAxiosError(e) && e.code === 'ERR_CANCELED') return true
  if (e instanceof Error && e.name === 'AbortError') return true
  return false
}

/** True when the request failed due to connectivity (offline, timeout, proxy/BFF down, etc.). */
export function isNetworkApiError(e: unknown): boolean {
  if (isAbortError(e)) return false
  if (e instanceof ApiError) {
    return isApiErrorNetworkFailure(e)
  }
  if (isAxiosError(e)) {
    return isAxiosNetworkFailure(e)
  }
  if (e instanceof Error) {
    return isErrorMessageNetworkFailure(e)
  }
  return false
}

export const TEAM_INVITE_SEND_NETWORK_ERROR_MESSAGE =
  'Invitation could not be sent. Please try again.'

/** Network/gateway toast when team invite accept (password setup) fails. */
export const TEAM_INVITE_ACCEPT_NETWORK_ERROR_MESSAGE =
  'Something went wrong. Please try again.'

/** Inline / form error when inviting Editor or Viewer fails due to network. */
export function messageFromTeamInviteSendError(e: unknown): string {
  if (isNetworkApiError(e)) {
    return TEAM_INVITE_SEND_NETWORK_ERROR_MESSAGE
  }
  if (e instanceof ApiError) {
    return e.message
  }
  if (e instanceof Error) {
    return e.message
  }
  return TEAM_INVITE_SEND_NETWORK_ERROR_MESSAGE
}

/** Toast copy for team invite password setup failures (network / gateway). */
export function messageFromTeamInvitePasswordError(e: unknown): string {
  if (isNetworkApiError(e)) {
    return TEAM_INVITE_ACCEPT_NETWORK_ERROR_MESSAGE
  }
  if (e instanceof ApiError) {
    return e.message
  }
  if (e instanceof Error) {
    return e.message
  }
  return TEAM_INVITE_ACCEPT_NETWORK_ERROR_MESSAGE
}
